'use client';

import React from 'react';

import { useTranslations } from 'next-intl';

import usePaymentFormContext from 'hooks/usePaymentFormContext';
import Input from 'ui/Input';
import SelectInput from 'ui/SelectInput';
import getCountryOptions from 'utils/getCountryOptions';
import AddressInputs from 'components/AddressInputs';

const EInvoicePaymentForm = () => {
  const {
    billingAddressData,
    billingAddressErrors,
    memberPhoneNumber,
    onChangeBillingAddressData,
    onChangePhoneNumber,
    phoneNumberDataErrors,
  } = usePaymentFormContext();

  const tCheckout = useTranslations('checkout');
  const tCountries = useTranslations('countries');
  const tProfile = useTranslations('customerProfile');
  const tCommon = useTranslations('common');
  const countryOptions = getCountryOptions();
  const translatedCountryOptions = countryOptions.map((country) => {
    return { value: country.value, label: tCountries(country.label) };
  });

  return (
    <div className="sm:col-span-6 flex flex-col gap-y-5 mt-2">
      <div className="sm:col-span-6 flex flex-col mt-2">
        <label className="font-bold text-sm">{tCheckout('phone').toLocaleUpperCase()}</label>
        <div className="relative mt-2 rounded-md shadow-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-outerSpace sm:text-sm">+47 &nbsp;|</span>
          </div>
          <input
            autoFocus
            type="tel"
            name="phone"
            id="phone"
            className="block w-full rounded border-0 py-3 pl-16 md:pl-14 text-outerSpace ring-1 ring-inset ring-dividerGrey placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            placeholder="123 45 678"
            value={memberPhoneNumber}
            onChange={onChangePhoneNumber}
            aria-describedby="price-currency"
          />
        </div>
        {phoneNumberDataErrors.phone ? (
          <p className="text-red mt-2">{phoneNumberDataErrors.phone}</p>
        ) : null}
      </div>
      <AddressInputs
        address={billingAddressData?.address ?? ''}
        addressTwo={billingAddressData?.addressTwo ?? ''}
        addressConfig={{
          name: 'address',
          id: 'address',
          label: tCommon('address').toLocaleUpperCase(),
          placeholder: tCommon('addressPlaceholder'),
        }}
        addressTwoConfig={{
          name: 'addressTwo',
          id: 'addressTwo',
          label: tCommon('addressLineTwo').toLocaleUpperCase(),
          placeholder: tCommon('addressLineTwoPlaceholder'),
        }}
        onChange={onChangeBillingAddressData}
        errors={{
          address: billingAddressErrors.address,
        }}
        addressClassName="sm:col-span-6 flex flex-col gap-y-1"
        addressTwoClassName="sm:col-span-6 flex flex-col gap-y-1"
        labelClassName="!font-bold text-sm"
      />
      <div className="flex flex-col sm:flex-row gap-y-5 sm:gap-x-2">
        <div className="flex flex-col gap-y-1 sm:w-2/5">
          <label className="font-bold text-sm">{tCheckout('zip').toLocaleUpperCase()}</label>
          <Input
            type="text"
            name="postCode"
            id="postCode"
            dataTestId="postCode"
            placeholder={tProfile('postCodePlaceholder')}
            value={billingAddressData.postCode}
            onChange={onChangeBillingAddressData}
            errorMessage={billingAddressErrors.postCode}
            errorBorderOnly
          />
        </div>
        <div className="flex flex-col gap-y-1 sm:w-3/5">
          <label className="font-bold text-sm">{tCheckout('city').toLocaleUpperCase()}</label>
          <Input
            type="text"
            name="city"
            id="city"
            dataTestId="city"
            placeholder={tCheckout('cityPlaceholder')}
            value={billingAddressData.city}
            onChange={onChangeBillingAddressData}
            errorMessage={billingAddressErrors.city}
            errorBorderOnly
          />
        </div>
      </div>
      <div className="sm:col-span-6 flex flex-col gap-y-1">
        <label className="font-bold text-sm">{tCheckout('country').toLocaleUpperCase()}</label>
        <SelectInput
          name="country"
          id="country"
          dataTestId="country"
          value={billingAddressData.country}
          options={translatedCountryOptions}
          textSize="text-md"
          onChange={onChangeBillingAddressData}
          errorMessage={billingAddressErrors.country}
          label=""
        />
      </div>
      <div className="flex flex-col gap-y-1">
        <label className="font-bold text-sm">
          {tCheckout('invoiceReference').toLocaleUpperCase()}
        </label>
        <Input
          type="text"
          name="invoiceReference"
          id="invoiceReference"
          dataTestId="invoiceReference"
          placeholder={tCheckout('invoiceReferencePlaceholder')}
          value={billingAddressData.invoiceReference ?? ''}
          onChange={onChangeBillingAddressData}
          errorMessage={billingAddressErrors.invoiceReference}
          errorBorderOnly
        />
      </div>
    </div>
  );
};

export default EInvoicePaymentForm;
